import { deriveSessionStatus, normalizeComparisonDimension, trainingFailure } from '../domain/training.ts';
import type { ExerciseRevision } from '../domain/training.ts';
import type { PerformanceFact, ProgressResult } from '../domain/progress.ts';
import type { TrainingRepository } from '../ports/training-repository.ts';
import type { TrainingProgressFacts, TrainingProgressSource } from '../ports/progress-sources.ts';
// Producer projection: public repository reads only; legacy absence is projected explicitly as UNKNOWN.
export class PublicTrainingProgress implements TrainingProgressSource {
  constructor(private history: Pick<TrainingRepository, 'listSessionHistory' | 'listExecutionHistory'>,
    private catalog: Pick<TrainingRepository, 'readExerciseRevision' | 'readContext'>) {}
  async readTrainingFacts(): Promise<ProgressResult<TrainingProgressFacts>> {
    const context = await this.catalog.readContext(); if (!context.ok) return context;
    const plans = await this.history.listSessionHistory(); if (!plans.ok) return plans;
    const executions = await this.history.listExecutionHistory(); if (!executions.ok) return executions;
    const sessions: TrainingProgressFacts['sessions'][number][] = [], performance: PerformanceFact[] = [];
    const byId = new Map(executions.value.map(e => [e.execution.executionId, e]));
    const definitions = new Map<string, ExerciseRevision>();
    for (const view of plans.value) {
      if (view.context.datasetId !== context.value.datasetId || view.context.controlEpoch !== context.value.controlEpoch) return trainingFailure('DATASET_STALE');
      const status = deriveSessionStatus(view.session, view.execution); if (!status.ok) return status;
      sessions.push({ sessionId: view.session.sessionId, cohortLocalDate: view.session.cohortLocalDate, status: status.value });
      if (!view.execution) continue;
      const detail = byId.get(view.execution.executionId);
      if (!detail || detail.context.datasetId !== view.context.datasetId || detail.context.controlEpoch !== view.context.controlEpoch
        || JSON.stringify(detail.execution) !== JSON.stringify(view.execution)) return trainingFailure('REVISION_CONFLICT');
      for (const set of detail.workingSets) {
        let exercise = definitions.get(set.exerciseRevisionRef);
        if (!exercise) {
          const result = await this.catalog.readExerciseRevision(set.exerciseRevisionRef); if (!result.ok) return result;
          exercise = result.value; definitions.set(set.exerciseRevisionRef, exercise);
        }
        performance.push({
          set,
          name: exercise.name,
          startedInstant: detail.execution.startedInstant,
          context: {
            exerciseId: exercise.exerciseId,
            variant: normalizeComparisonDimension(exercise.variant),
            equipment: normalizeComparisonDimension(exercise.equipment),
            configuration: normalizeComparisonDimension(exercise.configuration),
          },
        });
      }
    }
    performance.sort((a,b) => Date.parse(b.startedInstant) - Date.parse(a.startedInstant) || a.set.workingSetId.localeCompare(b.set.workingSetId));
    const after = await this.catalog.readContext(); if (!after.ok) return after;
    if (after.value.datasetId !== context.value.datasetId || after.value.controlEpoch !== context.value.controlEpoch) return trainingFailure('DATASET_STALE');
    return { ok: true, value: { sessions, performance } };
  }
}
